import React, { useCallback, useEffect, useState } from "react";
import {
  MobileSlideDialog,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  IconButton,
  Box,
  Divider,
  CloseIcon,
} from "../ui";

// Visual pattern shared by every confirmation dialog in the app.
// Primary = brand green (positive confirmations: "Yes, confirm", "Submit").
// Danger  = destructive red   (delete / remove / discard).
type Tone = "primary" | "danger";

const TONE_COLORS: Record<Tone, { bg: string; bgHover: string; disabledBg: string }> = {
  primary: { bg: "#003F44", bgHover: "#002b2f", disabledBg: "#7aa0a3" },
  danger: { bg: "#EF4444", bgHover: "#FF0F17", disabledBg: "#8300047a" },
};

export type ConfirmDialogProps = {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  /** Override the heading colour (defaults to the neutral dark used everywhere). */
  titleColor?: string;
  message: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  tone?: Tone;
  /** Disables both buttons + close while an async confirmation is in flight. */
  loading?: boolean;
  /** Disables the confirm button only (e.g. form still invalid). */
  confirmDisabled?: boolean;
  /** Override the default width for messages that contain lists or long content. */
  maxWidth?: "xs" | "sm" | "md";
};

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title = "Confirm",
  titleColor = "#111827",
  message,
  confirmText = "Yes",
  cancelText = "Cancel",
  tone = "primary",
  loading = false,
  confirmDisabled = false,
  maxWidth = "xs",
}) => {
  const colors = TONE_COLORS[tone];
  return (
    <MobileSlideDialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth={maxWidth}
      fullWidth
      keepMounted={false}
      sx={{ zIndex: 10000 }}
    >
      {/* DialogContent stretches on mobile (full-sheet) so the title + body
          sit at the top and the action buttons in DialogActions pin to the
          bottom of the sheet. On desktop the dialog hugs its content as
          before — DialogActions just sits below DialogContent in the flow. */}
      <DialogContent sx={{ p: 3, flex: 1 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, pb: 1.5 }}>
          <Typography sx={{ fontSize: 20, fontWeight: 700, color: titleColor, lineHeight: 1.3 }}>
            {title}
          </Typography>
          <IconButton
            size="small"
            onClick={onClose}
            disabled={loading}
            aria-label="Close"
            sx={{
              bgcolor: "#f3f4f6",
              width: 32,
              height: 32,
              color: "#6b7280",
              flexShrink: 0,
              "&:hover": { bgcolor: "#e5e7eb" },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        <Divider sx={{ borderColor: "#e5e7eb" }} />

        <Box sx={{ mt: 2.5, color: "#6b7280", lineHeight: 1.6, fontSize: 15 }}>
          {typeof message === "string" ? <Typography sx={{ color: "inherit" }}>{message}</Typography> : message}
        </Box>
      </DialogContent>

      {/* Sticky footer — pinned to the bottom of the bottom-sheet on mobile,
          mirroring the journey's UWStickyActionBar pattern. The border-top
          divider visually separates it from the scrollable content above. */}
      <DialogActions
        sx={{
          p: 3,
          gap: 2,
          borderTop: "1px solid #e5e7eb",
          backgroundColor: "#ffffff",
        }}
      >
        <Button
          fullWidth
          onClick={onClose}
          disabled={loading}
          sx={{
            flex: 1,
            py: 1.25,
            borderRadius: 2,
            color: "#111827",
            fontSize: "16px",
            fontWeight: 600,
            textTransform: "none",
            backgroundColor: "#f3f4f6",
            boxShadow: "none",
            "&:hover": { backgroundColor: "#e5e7eb", boxShadow: "none" },
            "&:disabled": { backgroundColor: "#f3f4f6", color: "#9ca3af" },
          }}
        >
          {cancelText}
        </Button>
        <Button
          fullWidth
          onClick={onConfirm}
          disabled={loading || confirmDisabled}
          sx={{
            flex: 1,
            py: 1.25,
            backgroundColor: colors.bg,
            borderRadius: 2,
            color: "#fff",
            fontSize: "16px",
            fontWeight: 600,
            textTransform: "none",
            boxShadow: "none",
            "&:hover": { backgroundColor: colors.bgHover, boxShadow: "none" },
            "&:disabled": { backgroundColor: colors.disabledBg, color: "#fff" },
          }}
        >
          {loading ? "Please wait…" : confirmText}
        </Button>
      </DialogActions>
    </MobileSlideDialog>
  );
};

// ─── Imperative API ──────────────────────────────────────────────────────────
// Existing callers use `openConfirmDialog({ title, message })` which returns a
// Promise<boolean>. Kept for backward compat; the host now renders through the
// same <ConfirmDialog> component so visuals stay in sync.

type ConfirmOptions = {
  title?: string;
  message: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  tone?: Tone;
};

type DialogHandler = (options: ConfirmOptions, resolve: (value: boolean) => void) => void;

let showDialog: DialogHandler | null = null;
const dialogHandlers: DialogHandler[] = [];

const registerDialogHost = (handler: DialogHandler) => {
  dialogHandlers.push(handler);
  showDialog = handler;
};

const unregisterDialogHost = (handler: DialogHandler) => {
  const index = dialogHandlers.lastIndexOf(handler);
  if (index !== -1) dialogHandlers.splice(index, 1);
  showDialog = dialogHandlers[dialogHandlers.length - 1] ?? null;
};

export const openConfirmDialog = (options: ConfirmOptions): Promise<boolean> => {
  return new Promise((resolve) => {
    if (!showDialog) {
      console.error("ConfirmDialogHost is not mounted.");
      resolve(false);
      return;
    }
    showDialog(options, resolve);
  });
};

export const ConfirmDialogHost: React.FC = () => {
  const [open, setOpen] = useState(false);
  // Default imperative tone is 'danger' — every existing imperative caller
  // is a destructive delete/remove action. Primary-tone confirmations should
  // use the declarative <ConfirmDialog> directly.
  const [options, setOptions] = useState<ConfirmOptions>({
    title: "Confirm",
    message: "",
    confirmText: "Yes",
    cancelText: "No",
    tone: "danger",
  });
  const [resolver, setResolver] = useState<(value: boolean) => void>(() => () => {});

  const dialogHandler = useCallback(
    (opts: ConfirmOptions, resolve: (value: boolean) => void) => {
      setOptions({
        title: opts.title ?? "Confirm",
        message: opts.message,
        confirmText: opts.confirmText ?? "Yes",
        cancelText: opts.cancelText ?? "No",
        tone: opts.tone ?? "danger",
      });
      setResolver(() => resolve);
      setOpen(true);
    },
    []
  );

  useEffect(() => {
    registerDialogHost(dialogHandler);
    return () => unregisterDialogHost(dialogHandler);
  }, [dialogHandler]);

  const handleClose = () => {
    setOpen(false);
    resolver(false);
  };

  const handleConfirm = () => {
    setOpen(false);
    resolver(true);
  };

  return (
    <ConfirmDialog
      open={open}
      onClose={handleClose}
      onConfirm={handleConfirm}
      title={options.title}
      message={options.message}
      confirmText={options.confirmText}
      cancelText={options.cancelText}
      tone={options.tone}
    />
  );
};
